import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { NavLink } from '~/eds/src/types/NavLink';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { usePermissions } from '~/hooks/usePermissions';
import { RoutePathType } from '~/routing/enums';
import {
  buildSidebarAdminNavLink,
  NAV_PERMISSIONS,
  useSidebarNavlinks,
} from '~/utils/navLinks';

const messages = defineMessages({
  home: {
    id: 'AppSidebarNav.home',
    defaultMessage: 'Home',
    description: 'Label for the sidebar link that navigates to the home page.',
  },
});

type Props = {
  /** Side effects the sidebar runs when a link navigates (resetting search,
   *  closing the mobile drawer) — owned by the component, not this hook. */
  onNavLinkClick: () => void;
};

/**
 * Resolves what the sidebar should show: the Workspace links, the pinned Home
 * and Admin entries, and whether Ask AI is available.
 *
 * Owns the permission and flag reads behind those decisions so the component
 * renders a list rather than recomputing the gating. `isLoading` reports the
 * permission round trip, so callers can tell "no access" apart from "not known
 * yet" — the two look identical in the items alone.
 */
export function useNavigationItems({ onNavLinkClick }: Props) {
  const intl = useIntl();
  const user = useCurrentUser();

  // The home route itself is flagged (EKP-58009), so the entry that points at
  // it has to be too — matching AppPageNavbar.
  const hasHome = useFlag(FlagType.Home);
  const hasAskAnything = useFlag(FlagType.AskAnything);

  // Same args as useSidebarNavlinks's internal call, deduped by the query cache.
  const { checkPermission, isLoading } = usePermissions({
    permissions: NAV_PERMISSIONS,
  });

  // Calls hooks internally, so it can't be wrapped in useMemo.
  const items = useSidebarNavlinks({ onNavLinkClick });

  const homeLink = useMemo<NavLink | null>(
    () =>
      hasHome
        ? {
            label: intl.formatMessage(messages.home),
            pathname: RoutePathType.Home,
            activePatterns: ['^/$'],
          }
        : null,
    [hasHome, intl],
  );

  // Pinned at the bottom of the sidebar, above the "A Workday Company"
  // credit — rather than scrolling with the rest of the Workspace links.
  const adminLink = useMemo(
    () => buildSidebarAdminNavLink(user, intl),
    [user, intl],
  );

  return {
    items,
    homeLink,
    adminLink,
    showAskAi: Boolean(
      hasAskAnything && checkPermission('conversational_ai', 'edit'),
    ),
    isLoading,
  };
}
